<script setup lang="ts">
// ================================
// IMPORTS
// ================================

// Vue ecosystem
import { useI18n } from "vue-i18n"
import { computed, ref, watch } from "vue"

// External libraries
import * as yup from "yup"
import { useMachine } from "@xstate/vue"

// Helpers and constants
import { findNestedObjectByKey } from "@core/helpers"
import { MESSAGES_DDA, PCE_DDA_STATUS } from "@core/constants"

// Types
import type { Source } from "@types"

// Machine
import { GrdfDDAMachine, useGrdfMachine } from "./grdf-dda.machine"

// ================================
// TYPES & INTERFACES
// ================================

/**
 * Données métier pour l'affichage du message d'erreur GRDF et les actions (formulaires, boutons).
 * Les valeurs sont dérivées du store source et auth dans le composant parent.
 */
export interface GrdfDdaMessageErrorContentProps {
    /** Identifiant du compteur (source) */
    meterId: string
    /** Identifiant du consentement PCE */
    idConsent: string
    /** Code postal utilisé pour la déclaration */
    zipcode: string
    /** Date de première donnée (début de contrat gaz) */
    dateFromData: string | null
    /** Date de fin du consentement (ISO) */
    dateLastConsent: string | null
    /** Statut de la DDA (PCE_DDA_STATUS) */
    status: string
    /** Raison du statut (pour OBSOLETE, REFUSED) */
    reason: string | null
    /** Code erreur (pour ERROR) */
    codeError: string | null
    /** Nom complet du titulaire du compteur */
    fullNameOwner: string
    /** Email du titulaire (pour les messages) */
    ownerUsername: string
    /** True si l'utilisateur connecté est le titulaire */
    isOwner: boolean
}

// ================================
// PROPS & EMITS
// ================================

const emit = defineEmits<{
    /** Émis quand la machine a récupéré une source (le parent met à jour le store) */
    "source-fetched": [source: Source]
}>()

const props = withDefaults(defineProps<GrdfDdaMessageErrorContentProps>(), {
    zipcode: "",
    dateFromData: null,
    dateLastConsent: null,
    reason: null,
    codeError: null,
    fullNameOwner: "",
    ownerUsername: "",
})

// ================================
// INTERNATIONALIZATION
// ================================

const obsoleteCase1 = () => {
    if (props.dateFromData) {
        return `Le droit d'accès avec GRDF a expiré. ${
            props.isOwner
                ? "Veuillez cliquer sur renvoyer un mail de GRDF (nepasrepondre@notification.grdf.fr) pour renouveler votre droit d'accès et continuer de partager vos données de consommation."
                : `Vous pouvez renvoyer un mail à <strong>${props.fullNameOwner}</strong> pour renouveler son droit d'accès avec GRDF et continuer le partage des données de consommation.`
        }`
    }
    return props.isOwner
        ? "Le droit d'accès avec GRDF a expiré. Afin de le renouveler, veuillez renseigner votre date de début de contrat et cliquer sur renvoyer le mail."
        : "Le droit d'accès avec GRDF a expiré. Afin de le renouveler, il faut que le titulaire accède à la plateforme Homeys pour finir la procédure."
}

const { t } = useI18n({
    messages: {
        fr: {
            state: {
                [PCE_DDA_STATUS.A_VALIDER]: () => {
                    const username = props.ownerUsername || ""
                    if (props.isOwner) {
                        return `Pour réaliser l'analyse de vos données de gaz, veuillez valider le mail envoyé par <a class="underline" href="mailto:nepasrepondre@notification.grdf.fr">nepasrepondre@notification.grdf.fr</a> à l'adresse : <a class="font-bold" href="mailto:${username}">${username}</a> intitulé "nouvelle demande d'accès à vos données gaz" dans un délai de <strong>3 jours</strong>. Dès que vous avez validé le mail, cliquez sur le bouton "J'ai validé les droits d'accès" ci-dessous pour mettre à jour les données contractuelles de votre compteur de gaz.`
                    }
                    return "Le titulaire n'a pas encore validé les droits d'accès. Il dispose d'un délai de <strong>3 jours</strong> à partir la réception du mail GRDF pour finaliser la procédure."
                },
                [PCE_DDA_STATUS.OBSOLETE]: {
                    case_1: obsoleteCase1,
                    case_2: obsoleteCase1,
                    case_3: "Le consentement avec GRDF a expiré par les services de GRDF directement. Si votre consentement Homeys est toujours valide, vous pouvez relancer la déclaration de droit d'accès ci-dessous. En cas de blocage, contactez le support GRDF.",
                    case_4: "Le compteur de gaz a changé de titulaire selon GRDF. Si le consentement Homeys a été refait au nom du nouveau titulaire, vous pouvez relancer la déclaration de droit d'accès ci-dessous : le titulaire recevra un mail GRDF de validation. Sinon, contactez le support GRDF pour plus d'informations.",
                    case_5: "L'adresse email du titulaire n'est plus valide, ce dernier n'a donc pas reçu le mail d'information concernant le renouvellement du consentement GRDF. Si l'adresse a été corrigée dans le consentement Homeys, vous pouvez relancer la déclaration ci-dessous. Sinon, veuillez contacter notre support.",
                },
                [PCE_DDA_STATUS.REFUSED]: {
                    case_1: () =>
                        props.isOwner
                            ? "Il semblerait que vous n'ayez pas reçu le courriel de demande de validation du droit d'accès. Veuillez contacter le support GRDF pour plus d'informations si le problème persiste."
                            : "Le titulaire n'a pas reçu le courriel de demande de validation du droit d'accès. Veuillez contacter le support GRDF pour plus d'informations si le problème persiste.",
                    case_2: () =>
                        props.isOwner
                            ? "La demande d'accès a été refusée depuis le mail envoyé par GRDF. Vous pouvez relancer une nouvelle demande ci-dessous : un nouveau mail de validation vous sera envoyé. En cas de blocage, contactez le support GRDF."
                            : "La demande d'accès a été refusée par le titulaire depuis le mail envoyé. Vous pouvez relancer une nouvelle demande ci-dessous : le titulaire recevra un nouveau mail de validation. En cas de refus répété, contactez le support GRDF.",
                    case_3: () => {
                        const message =
                            "La durée de validité de la demande d'accréditation est dépassée car elle n'a pas été validée ou refusée dans le délai de 3 jours. "
                        if (!props.dateFromData && !props.isOwner)
                            return (
                                message +
                                "Il faut que le titulaire du compteur accède à la plateforme Homeys pour finir la procédure."
                            )
                        if (!props.dateFromData && props.isOwner)
                            return (
                                message +
                                "Veuillez d'abord renseigner votre date de début de contrat puis refaire une nouvelle demande en recevant le mail de GRDF (nepasrepondre@notification.grdf.fr) pour partager vos données de consommation."
                            )
                        return (
                            message +
                            (props.isOwner
                                ? "Veuillez refaire une nouvelle demande en recevant le mail de GRDF (nepasrepondre@notification.grdf.fr) pour partager vos données de consommation."
                                : `Vous pouvez renvoyer le mail GRDF au titulaire <strong>${props.fullNameOwner}</strong> pour qu'il puisse partager ses données de consommation.`)
                        )
                    },
                    case_4: "Le compteur de gaz a changé de titulaire selon GRDF. Si le consentement Homeys a été refait au nom du nouveau titulaire, vous pouvez relancer la déclaration de droit d'accès ci-dessous : le titulaire recevra un mail GRDF de validation. Sinon, contactez le support GRDF pour plus d'informations.",
                },
                [PCE_DDA_STATUS.REVOKED]:
                    "La déclaration auprès de GRDF a été révoqué. Si cela est une erreur, veuillez contacter le support GRDF.",
                [PCE_DDA_STATUS.EMPTY]: () =>
                    `Aucune déclaration auprès de GRDF n'a été faite. Veuiller cliquer sur le bouton ci-dessous pour déclarer auprès de GRDF si vous êtes bien le titulaire de ce compteur de gaz. Un email sera envoyé à l'adresse <strong>${props.ownerUsername}</strong>.`,
                [PCE_DDA_STATUS.ERROR]: {
                    case_1: () =>
                        `Une erreur s'est produite lors de la déclaration auprès de GRDF. La date de début du périmètre de consommation est obligatoire (format attendu AAAA-MM-JJ). ${props.isOwner ? "Veuillez renseigner la date de début de votre contrat de gaz ci-dessous puis valider." : "Afin de régler le problème, il faut que le titulaire accède à la plateforme Homeys pour renseigner cette date."}`,
                    case_7: () =>
                        `Une erreur s'est produite lors de la déclaration auprès de GRDF. La date de début du périmètre de consommation doit être antérieure ou égale à la date du jour. ${props.isOwner ? "Veuillez corriger la date ci-dessous puis valider." : "Il faut que le titulaire corrige la date depuis la plateforme Homeys."}`,
                    case_2: "Le numéro de PCE saisi est inconnu. Vous pouvez corriger ça en ajoutant un nouveau compteur dans la page contrat de votre bâtiment. Si vous souhaitez obtenir plus d'informations, utilisez le bouton Aide en bas à droite de votre écran.",
                    case_3: () => {
                        if (props.dateFromData) {
                            return `Le code postal renseigné ne correspond à celui du PCE. Veuillez vérifier et corriger le code postal puis renvoyer le mail de droit d'accès${props.isOwner ? "." : ` à <strong>${props.fullNameOwner}</strong>.`}`
                        }
                        if (!props.dateFromData && props.isOwner)
                            return "Il semblerait que le code postal renseigné ne correspond à celui du PCE et qu'il manque la date de début de contrat de gaz. Veuillez vérifier et corriger le code postal et la date de début de contrat et le puis renvoyer le mail de droit d'accès GRDF."
                        return "Le code postal renseigné ne correspond à celui du PCE. Cependant, il faut que le titulaire accède à la plateforme Homeys pour renseigner la date de début de son contrat de gaz avant de pouvoir corriger le code postal."
                    },
                    case_6: "Le contrat de fourniture de gaz est échu. Veuillez contacter le support GRDF pour plus d'informations.",
                    unknown_error: () =>
                        `GRDF nous renvoie le code d'erreur : <strong>${props.codeError ?? ""}</strong> avec le message suivant "<strong>${props.reason ?? ""}</strong>"`,
                },
                unknown_error:
                    "Une erreur inconnue est survenue. Il est possible qu'aucun droit d'accès valide n'ait été créé. Vous pouvez relancer une demande de droit d'accès ou contacter le support GRDF pour en savoir plus.",
            },
            zipcode: {
                header: "Vous pouvez :",
                advices: {
                    advice_1:
                        "Vérifier que le compteur est bien à cette adresse.",
                    advice_2:
                        "Essayer avec l'ancien code postal (ex : 75116 pour 75016) ou le code INSEE.",
                    advice_3:
                        "Regarder le code postal sur la facture ou en vous connectant à votre espace GRDF.",
                },
                placeholder: "Ex: 75015",
                submit: "Valider le code postal",
            },
            date_from_data: {
                advice: "Pour continuer la procédure avec GRDF, veuillez renseigner une date de début de votre contrat de gaz puis cliquer sur valider pour passer à la suite. Cette date doit être antérieure ou égale à la date de début de votre consentement Homeys.",
                placeholder: "--/--/----",
                submit: "Valider cette date de début de contrat",
            },
            create_dda: {
                explain: "Une nouvelle déclaration est requise auprès de GRDF.",
                loading:
                    "Création de la déclaration de droit d'accès en cours...",
                button: "Relancer la déclaration de droit d'accès",
                button_declare: "Lancer la déclaration de droit d'accès",
                back_to_edit_date: "Modifier la date de début de contrat",
                back_to_edit_zipcode: "Modifier le code postal",
                consent_expired: () =>
                    props.isOwner
                        ? "La date de fin de votre consentement Homeys est dépassée. Veuillez d'abord prolonger ou renouveler votre consentement dans Homeys ; vous pourrez ensuite terminer la déclaration auprès de GRDF."
                        : `La date de fin du consentement Homeys du titulaire est dépassée. Demandez à <strong>${props.fullNameOwner}</strong> de prolonger son consentement dans Homeys avant de poursuivre la déclaration GRDF.`,
            },
            update_dda: "J'ai validé les droits d'accès",
            update_dda_retry: "Réessayer",
            update_dda_still_pending:
                "D'après GRDF, la demande est toujours en attente de votre validation. Vérifiez le mail GRDF (et vos spams), cliquez le lien de validation, puis réessayez.",
            update_dda_error_generic:
                "La mise à jour auprès de GRDF n'a pas abouti. Vous pouvez réessayer ou contacter le support si le problème persiste.",
            refresh_grdf: "Vérifier l'état auprès de GRDF",
            support_grdf: "Contacter le support GRDF pour plus d'informations",
            /** DDA au statut Active : message de succès (hors bandeau erreur / à valider). */
            active_validated:
                "La demande de droit d'accès a bien été validée auprès de GRDF.",
        },
    },
})

// ================================
// COMPUTED PROPERTIES
// ================================

/**
 * Message à afficher en fonction du statut de la déclaration de droit d'accès GRDF
 */
const getMessage = computed(() => {
    switch (props.status) {
        case PCE_DDA_STATUS.A_VALIDER:
            return t(`state.${props.status}`)
        case PCE_DDA_STATUS.OBSOLETE: {
            switch (props.reason) {
                case MESSAGES_DDA.OBSOLETE.CASE_1:
                    return t(`state.${props.status}.case_1`)
                case MESSAGES_DDA.OBSOLETE.CASE_2:
                    return t(`state.${props.status}.case_2`)
                case MESSAGES_DDA.OBSOLETE.CASE_3:
                    return t(`state.${props.status}.case_3`)
                case MESSAGES_DDA.OBSOLETE.CASE_4:
                    return t(`state.${props.status}.case_4`)
                case MESSAGES_DDA.OBSOLETE.CASE_5:
                    return t(`state.${props.status}.case_5`)
                default:
                    return ""
            }
        }
        case PCE_DDA_STATUS.REFUSED:
            switch (props.reason) {
                case MESSAGES_DDA.REFUSED.CASE_1:
                    return t(`state.${props.status}.case_1`)
                case MESSAGES_DDA.REFUSED.CASE_2:
                    return t(`state.${props.status}.case_2`)
                case MESSAGES_DDA.REFUSED.CASE_3:
                    return t(`state.${props.status}.case_3`)
                case MESSAGES_DDA.REFUSED.CASE_4:
                    return t(`state.${props.status}.case_4`)
                default:
                    return ""
            }
        case PCE_DDA_STATUS.ERROR: {
            switch (props.codeError) {
                case MESSAGES_DDA.ERROR.CASE_1:
                    return t(`state.${props.status}.case_1`)
                case MESSAGES_DDA.ERROR.CASE_7:
                    return t(`state.${props.status}.case_7`)
                case MESSAGES_DDA.ERROR.CASE_2:
                    return t(`state.${props.status}.case_2`)
                case MESSAGES_DDA.ERROR.CASE_3:
                case MESSAGES_DDA.ERROR.CASE_4:
                case MESSAGES_DDA.ERROR.CASE_5:
                    return t(`state.${props.status}.case_3`)
                case MESSAGES_DDA.ERROR.CASE_6:
                    return t(`state.${props.status}.case_6`)
                default:
                    return t(`state.${props.status}.unknown_error`)
            }
        }
        case PCE_DDA_STATUS.REVOKED:
            return t(`state.${props.status}`)
        case PCE_DDA_STATUS.ACTIVE:
            return t("active_validated")
        default:
            return t(`state.unknown_error`)
    }
})

/**
 * Indique si on doit afficher le formulaire de modification du code postal (erreur GRDF liée au CP).
 * Ne pas dépendre de la présence de `update_field` dans le snapshot : après succès API on est dans
 * `create_dda_workflow` tout en devant garder le formulaire visible (champs désactivés).
 */
const hasErrorZipcode = computed(() => {
    return (
        props.status === PCE_DDA_STATUS.ERROR &&
        [
            MESSAGES_DDA.ERROR.CASE_3,
            MESSAGES_DDA.ERROR.CASE_4,
            MESSAGES_DDA.ERROR.CASE_5,
        ].includes(props.codeError as string)
    )
})

/**
 * Erreurs GRDF liées au champ date de début (périmètre conso) : obligatoire / format (1000000041) ou incohérence date (1000000019).
 * Ne pas utiliser CASE_5 (1000000005 = code postal ne correspond pas au PCE).
 */
const ERROR_CODES_DATE_FROM_DATA = [
    MESSAGES_DDA.ERROR.CASE_1,
    MESSAGES_DDA.ERROR.CASE_7,
] as const

/**
 * Indique si on doit afficher le formulaire de modification de la date de début de contrat
 */
const hasErrorDateFromData = computed(() => {
    return (
        props.status === PCE_DDA_STATUS.ERROR &&
        ERROR_CODES_DATE_FROM_DATA.includes(
            props.codeError as (typeof ERROR_CODES_DATE_FROM_DATA)[number]
        )
    )
})

/**
 * Sous-état `update_consent` du workflow de (re)déclaration : le consentement est valide mais
 * sans date de début de données — il faut afficher le formulaire date, sinon l'utilisateur est
 * en impasse (aucun bouton rendu pour ce sous-état avant DEV-674).
 */
const isInUpdateConsentSubstate = computed(() => {
    const branch = findNestedObjectByKey(
        snapshot.value.value as Record<string, unknown>,
        "update_consent"
    )
    return branch !== null && branch !== undefined
})

/**
 * États « bloqués » désormais redéclarables (DEV-674) : la machine y monte le workflow
 * create_dda (il n'y a donc plus de sous-état support dans le snapshot), mais le message
 * référence toujours le support et l'utilisateur doit garder « Vérifier l'état » (et le
 * bouton support GRDF quand le message y renvoie).
 */
const isBlockedRedeclarableState = computed(
    () =>
        (props.status === PCE_DDA_STATUS.OBSOLETE &&
            [
                MESSAGES_DDA.OBSOLETE.CASE_3,
                MESSAGES_DDA.OBSOLETE.CASE_4,
                MESSAGES_DDA.OBSOLETE.CASE_5,
            ].includes(props.reason as string)) ||
        (props.status === PCE_DDA_STATUS.REFUSED &&
            [MESSAGES_DDA.REFUSED.CASE_2, MESSAGES_DDA.REFUSED.CASE_4].includes(
                props.reason as string
            ))
)

/**
 * Parmi les états bloqués redéclarables, ceux dont le message renvoie au support GRDF
 * (Obsolète case 5 renvoie au support Homeys — pas de bouton GRDF, comme avant DEV-674).
 */
const isGrdfSupportRelevantBlockedState = computed(
    () =>
        (props.status === PCE_DDA_STATUS.OBSOLETE &&
            [
                MESSAGES_DDA.OBSOLETE.CASE_3,
                MESSAGES_DDA.OBSOLETE.CASE_4,
            ].includes(props.reason as string)) ||
        (props.status === PCE_DDA_STATUS.REFUSED &&
            [MESSAGES_DDA.REFUSED.CASE_2, MESSAGES_DDA.REFUSED.CASE_4].includes(
                props.reason as string
            ))
)

/**
 * Affiche le bouton « Contacter le support GRDF » : état final support GRDF dans la machine,
 * statut Empty (déclaration jamais faite — lien utile même hors sous-état support), ou état
 * bloqué redéclarable dont le message renvoie au support GRDF (DEV-674).
 */
const showGrdfSupportButton = computed(() => {
    if (props.status === PCE_DDA_STATUS.EMPTY) return true
    if (isGrdfSupportRelevantBlockedState.value) return true
    return JSON.stringify(snapshot.value.value).includes("support_grdf")
})

/**
 * Affiche le bouton « Vérifier auprès de GRDF » (re-consult live) sur TOUS les états
 * terminaux où l'utilisateur serait sinon en cul-de-sac : aussi bien les états support
 * GRDF (`support_grdf` : Obsolète case 3/4, Révoqué tiers, Erreur contrat échu / PCE
 * inconnu…) que les états support non-GRDF (`support` : Révoqué, Obsolète case 5 email
 * invalide). Garantit qu'aucun état terminal n'est sans action — l'utilisateur peut
 * toujours resynchroniser l'état réel GRDF (et corriger une divergence côté Homeys).
 * Note : `support_grdf` contient `support`, donc le match sur "support" couvre les deux.
 */
const showGrdfRefreshButton = computed(() => {
    // DEV-674 : les états bloqués redéclarables n'ont plus de sous-état support dans la
    // machine — on garde explicitement le bouton « Vérifier l'état » (resync live GRDF).
    if (isBlockedRedeclarableState.value) return true
    return JSON.stringify(snapshot.value.value).includes("support")
})

/**
 * Indique si un rafraîchissement live (état `refreshing`) est en cours.
 */
const isRefreshingFromGrdf = computed(() => {
    const v = snapshot.value.value
    return typeof v === "object" && v !== null && "refreshing" in v
})

/**
 * Libellé du bouton principal de déclaration GRDF (première déclaration vs relance).
 */
const getCreateDdaButtonLabel = computed(() =>
    props.status === PCE_DDA_STATUS.EMPTY
        ? t("create_dda.button_declare")
        : t("create_dda.button")
)

/**
 * Permet de connaître le statut lors de la mise à jour des données contractuelles du compteur.
 */
const getReloadSourceState = computed(() => {
    const status = findNestedObjectByKey(snapshot.value.value, "reload_source")
    return {
        loading: status === "loading",
        success: status === "success",
        failure: status === "failure",
    }
})

/**
 * Permet de connaitre le statut de la création de la déclaration de droit d'accès.
 */
const getCreateDdaState = computed(() => {
    const status = findNestedObjectByKey(snapshot.value.value, "create_dda")
    return {
        current: !!status,
        loading: status === "loading",
        success: status === "success",
        failure: status === "failure",
    }
})

/**
 * Blocage « consentement Homeys expiré » avant / pendant la correction GRDF :
 * état final `consent_expired` du sous-workflow, ou consentement déjà expiré côté props sur les parcours date / code postal (la machine n’y arrive qu’après succès API sans play en Storybook).
 */
const isCreateDdaWorkflowConsentExpired = computed(() => {
    const branch = findNestedObjectByKey(
        snapshot.value.value,
        "create_dda_workflow"
    )
    const machineExpired =
        branch === "consent_expired" ||
        (branch !== null &&
            typeof branch === "object" &&
            Object.prototype.hasOwnProperty.call(branch, "consent_expired"))
    if (machineExpired) return true

    if (!props.dateLastConsent) return false
    const consentEndMs = new Date(props.dateLastConsent).getTime()
    if (Number.isNaN(consentEndMs) || consentEndMs > Date.now()) return false

    return hasErrorDateFromData.value || hasErrorZipcode.value
})

/**
 * Phase « relancer la déclaration » : sous-état `create_dda` actif — saisie date / CP verrouillée, bouton retour édition proposé.
 */
const isGrdfErrorFieldEditLocked = computed(
    () =>
        getCreateDdaState.value.current &&
        !isCreateDdaWorkflowConsentExpired.value &&
        (hasErrorDateFromData.value || hasErrorZipcode.value)
)

/**
 * Libellé du bouton pour revenir corriger la date ou le code postal selon le parcours erreur.
 */
const getBackToEditGrdfFieldLabel = computed(() =>
    hasErrorDateFromData.value
        ? t("create_dda.back_to_edit_date")
        : t("create_dda.back_to_edit_zipcode")
)

/**
 * Branche machine XState v5 pour le workflow « À valider » (chaîne feuille ou objet imbriqué).
 */
function getAValiderBranch(
    machineValue: unknown
): string | Record<string, unknown> | undefined {
    return findNestedObjectByKey(
        machineValue as Record<string, unknown>,
        PCE_DDA_STATUS.A_VALIDER
    )
}

/**
 * Indique si le sous-état « À valider » correspond à une clé donnée (compatible valeur string ou objet XState v5).
 */
function isAValiderSubstate(machineValue: unknown, substate: string): boolean {
    const branch = getAValiderBranch(machineValue)
    if (branch === substate) return true
    if (
        branch !== null &&
        typeof branch === "object" &&
        !Array.isArray(branch)
    ) {
        return Object.prototype.hasOwnProperty.call(branch, substate)
    }
    return false
}

/**
 * Permet de connaître le statut lors de la récupération du dernier état de la déclaration de droit d'accès.
 */
const getLastDeclarationState = computed(() => {
    const v = snapshot.value.value
    return {
        loading: isAValiderSubstate(v, "loading"),
        success: isAValiderSubstate(v, "success"),
        failure: isAValiderSubstate(v, "failure"),
    }
})

/**
 * Affiche le bouton pour rafraîchir la déclaration GRDF et recharger la source (statut métier « À valider »).
 * L’ancienne condition testait une chaîne « idle » / « failure » : avec XState v5 la valeur est souvent un objet
 * ({ idle: {} }, { reload_source: … }), ce qui masquait le bouton alors que le texte invitait à cliquer.
 */
const hasNeedToUpdateDda = computed(() => {
    return snapshot.value.context.status === PCE_DDA_STATUS.A_VALIDER
})

/**
 * Indique si le bouton "Mettre à jour l'état de la déclaration de droit d'accès" est en cours de chargement.
 */
const isLoadingButtonUpdateDda = computed(() => {
    return (
        getReloadSourceState.value.loading ||
        getLastDeclarationState.value.loading
    )
})

/**
 * Vrai si le dernier re-check live a échoué techniquement (sous-état failure de A_VALIDER).
 * On affiche alors un message clair générique — sans exposer la réponse brute GRDF.
 */
const hasUpdateFailed = computed(() => {
    return isAValiderSubstate(snapshot.value.value, "failure")
})

/**
 * Vrai quand un re-check "J'ai validé" a été fait mais que GRDF répond toujours "À valider"
 * (le titulaire n'a pas encore validé le mail). Sert à afficher un retour explicite + invitation à réessayer.
 */
const isStillPendingAfterCheck = computed(() => {
    return (
        snapshot.value.context.status === PCE_DDA_STATUS.A_VALIDER &&
        snapshot.value.context.update_checked === true &&
        !isLoadingButtonUpdateDda.value &&
        !isAValiderSubstate(snapshot.value.value, "failure")
    )
})

/**
 * Indique si le bouton "Relancer la déclaration de droit d'accès" est en cours de chargement.
 */
const isLoadingButtonCreateDda = computed(() => {
    return getCreateDdaState.value.loading || getReloadSourceState.value.loading
})

// ================================
// XState Machine
// ================================

const { machine } = useGrdfMachine()

const machineInput = computed(() => ({
    _id_source: props.meterId,
    _id_consent: props.idConsent,
    zipcode: props.zipcode,
    date_from_data: props.dateFromData,
    date_last_consent: props.dateLastConsent,
    status: props.status || PCE_DDA_STATUS.EMPTY,
    reason: props.reason,
    code_error: props.codeError,
}))

const { snapshot, send } = useMachine<GrdfDDAMachine>(machine, {
    /** Objet plain : ne pas passer la ComputedRef (sinon contexte par défaut Refusée + mauvaise branche XState, ex. Storybook). */
    input: machineInput.value,
})

// ================================
// Gestion des formulaires
// ================================

const schemas = {
    zipcode: yup
        .string()
        .required("Le code postal est requis")
        .matches(/^[0-9]{5}$/, "Le code postal doit contenir 5 chiffres"),
    date_from_data: yup
        .date()
        .required("La date est requise")
        .max(new Date(), "La date ne peut pas être dans le futur"),
}

const errors = ref({
    zipcode: null as string | null,
    date_from_data: null as string | null,
})

const validateField = async (field: keyof typeof schemas, value: unknown) => {
    if (!value) {
        errors.value[field] = null
        return
    }
    try {
        await schemas[field].validate(value)
        errors.value[field] = null
    } catch (error) {
        if (error instanceof yup.ValidationError) {
            errors.value[field] = error.message
        }
    }
}

watch(
    () => snapshot.value.context.zipcode,
    (newValue) => validateField("zipcode", newValue)
)
watch(
    () => snapshot.value.context.date_from_data,
    (newValue) => validateField("date_from_data", newValue)
)

/**
 * Quand la machine a récupéré une source (reload_source success), l'émettre au parent pour mise à jour du store.
 */
watch(
    () => snapshot.value.context._lastFetchedSource,
    (source) => {
        if (source) {
            emit("source-fetched", source)
            send({ type: "CLEAR_LAST_FETCHED" })
        }
    }
)

// ================================
// Synchronisation du contexte machine avec les props
// ================================

watch(
    machineInput,
    (newInput) => {
        send({
            type: "RELOAD_CONTEXT",
            data: {
                _id_source: newInput._id_source,
                _id_consent: newInput._id_consent,
                zipcode: newInput.zipcode,
                status: newInput.status,
                reason: newInput.reason ?? undefined,
                code_error: newInput.code_error ?? undefined,
                date_last_consent: newInput.date_last_consent ?? undefined,
                date_from_data: newInput.date_from_data ?? undefined,
            },
        })
    },
    { deep: true }
)

// ================================
// Redirection automatique de la machine
// ================================

watch(
    () => snapshot.value.value,
    (newValue) => {
        if (typeof newValue === "string" && newValue === "idle") {
            send({ type: "REDIRECTION" })
        }
    }
)

// ================================
// Handlers
// ================================

function onClickContactSupport() {
    if (showGrdfSupportButton.value) {
        window.open(
            "https://sites.grdf.fr/web/portail-api-grdf-adict/support-utilisateurs",
            "_blank"
        )
    }
}

const isDevMode = computed(() => import.meta.env.MODE === "development")

/** DDA validée côté GRDF : message de succès dédié, sans le bandeau erreur / formulaires. */
const isDdaActive = computed(() => props.status === PCE_DDA_STATUS.ACTIVE)

/**
 * Sévérité du bandeau principal : avertissement pour l'état « À valider », erreur sinon.
 */
const getMainMessageSeverity = computed(() =>
    props.status === PCE_DDA_STATUS.A_VALIDER ? "warn" : "error"
)
</script>

<template>
    <BaseMessage class="my-2" v-if="isDevMode">
        <pre>{{ snapshot.value }}</pre>
        <pre>{{ snapshot.context }}</pre>
    </BaseMessage>
    <BaseMessage
        v-if="isDdaActive"
        severity="success"
        class="my-2 w-full"
        :closable="false"
    >
        <p>{{ t("active_validated") }}</p>
    </BaseMessage>
    <BaseMessage v-else :severity="getMainMessageSeverity">
        <p v-html="getMessage"></p>

        <!-- Consentement Homeys expiré : prolongation requise avant déclaration GRDF -->
        <div v-if="isCreateDdaWorkflowConsentExpired" class="mt-6">
            <p v-html="t('create_dda.consent_expired')"></p>
        </div>

        <!-- Cas où il faut modifier le code postal -->
        <div
            v-if="hasErrorZipcode && !isCreateDdaWorkflowConsentExpired"
            class="mt-6 flex flex-col gap-y-6"
        >
            <div class="flex flex-col gap-y-6">
                <p>
                    {{ t("zipcode.header") }}
                </p>
                <ul class="list-disc list-inside">
                    <li v-for="index in 3" :key="index">
                        {{ t(`zipcode.advices.advice_${index}`) }}
                    </li>
                </ul>
            </div>

            <div class="flex flex-col gap-y-4">
                <BaseInputWrapper
                    class="mt-6"
                    input-id="zipcode"
                    unfloat
                    :error="errors.zipcode"
                >
                    <BaseInputText
                        :invalid="!!errors.zipcode"
                        :disabled="isGrdfErrorFieldEditLocked"
                        :value="snapshot.context.zipcode"
                        @update:model-value="
                            send({ type: 'UPDATE_ZIPCODE', zipcode: $event })
                        "
                        input-id="zipcode"
                        :placeholder="t('zipcode.placeholder')"
                    />
                </BaseInputWrapper>
                <BaseButton
                    severity="danger"
                    :disabled="
                        isGrdfErrorFieldEditLocked ||
                        !!errors.zipcode ||
                        !snapshot.context.zipcode
                    "
                    @click="send({ type: 'START' })"
                    :label="t('zipcode.submit')"
                >
                </BaseButton>
            </div>
        </div>

        <!-- Cas où il faut modifier la date de première donnée (erreur GRDF date, ou
             sous-état update_consent du workflow de redéclaration : consentement sans date) -->
        <div
            v-if="
                (hasErrorDateFromData || isInUpdateConsentSubstate) &&
                !isCreateDdaWorkflowConsentExpired
            "
            class="mt-6 flex flex-col gap-y-6"
        >
            <p>
                {{ t("date_from_data.advice") }}
            </p>
            <div class="flex flex-col gap-y-4">
                <BaseInputWrapper
                    class="mt-6"
                    unfloat
                    :error="errors.date_from_data"
                    input-id="date-from-data"
                >
                    <BaseDatePicker
                        :disabled="isGrdfErrorFieldEditLocked"
                        :value="snapshot.context.date_from_data"
                        @update:model-value="
                            send({
                                type: 'UPDATE_DATE_FROM_DATA',
                                date_from_data: $event,
                            })
                        "
                        :invalid="!!errors.date_from_data"
                        date-format="dd/mm/yy"
                        show-icon
                        :max-date="new Date()"
                        icon-display="input"
                        input-id="date-from-data"
                        :placeholder="t('date_from_data.placeholder')"
                    />
                </BaseInputWrapper>
                <BaseButton
                    severity="danger"
                    :disabled="
                        isGrdfErrorFieldEditLocked ||
                        !!errors.date_from_data ||
                        !snapshot.context.date_from_data
                    "
                    @click="send({ type: 'START' })"
                    :label="t('date_from_data.submit')"
                ></BaseButton>
            </div>
        </div>

        <!-- Cas où il faut relancer une déclaration -->
        <div
            v-if="getCreateDdaState.current"
            class="mt-6 flex flex-wrap items-center gap-3"
        >
            <BaseButton
                v-if="isGrdfErrorFieldEditLocked"
                outlined
                severity="secondary"
                :disabled="isLoadingButtonCreateDda"
                :label="getBackToEditGrdfFieldLabel"
                @click="send({ type: 'BACK_TO_EDIT_FIELD' })"
            />
            <BaseButton
                severity="danger"
                @click="send({ type: 'START' })"
                :loading="isLoadingButtonCreateDda"
                :icon="isLoadingButtonCreateDda ? 'pi pi-spin pi-spinner' : ''"
                icon-pos="right"
                :label="getCreateDdaButtonLabel"
            />
        </div>

        <!-- Cas où il faut mettre à jour l'état de la déclaration (le titulaire vient de valider) -->
        <div v-if="hasNeedToUpdateDda" class="mt-6">
            <!-- Retour explicite quand GRDF répond "toujours en attente de validation" après un re-check -->
            <p
                v-if="isStillPendingAfterCheck"
                class="mb-3 text-sm text-amber-700"
            >
                {{ t("update_dda_still_pending") }}
            </p>
            <!-- Échec technique du re-check : message clair générique (pas de réponse brute GRDF) -->
            <p v-if="hasUpdateFailed" class="mb-3 text-sm text-red-700">
                {{ t("update_dda_error_generic") }}
            </p>
            <BaseButton
                @click="send({ type: 'START' })"
                :loading="isLoadingButtonUpdateDda"
                :icon="isLoadingButtonUpdateDda ? 'pi pi-spin pi-spinner' : ''"
                icon-pos="right"
                :label="
                    isStillPendingAfterCheck || hasUpdateFailed
                        ? t('update_dda_retry')
                        : t('update_dda')
                "
            />
        </div>

        <div
            class="mt-6 flex flex-wrap items-center gap-3"
            v-if="showGrdfSupportButton || showGrdfRefreshButton"
        >
            <!-- Rafraîchir l'état réel auprès de GRDF (re-consult live) sur les états terminaux -->
            <BaseButton
                v-if="showGrdfRefreshButton"
                outlined
                severity="secondary"
                size="sm"
                @click="send({ type: 'REFRESH' })"
                :loading="isRefreshingFromGrdf"
                :icon="isRefreshingFromGrdf ? 'pi pi-spin pi-spinner' : ''"
                icon-pos="right"
                :label="t('refresh_grdf')"
            />
            <BaseButton
                class="meter-pce-tracking-error__button-task"
                variant="danger"
                :solid="true"
                size="sm"
                :label="t('support_grdf')"
                @click="onClickContactSupport"
            />
        </div>
    </BaseMessage>
</template>
